import React, { useCallback, useEffect, useMemo, useState } from "react";
import banner from "../../assets/images/banner.png";
import {
  getBanners,
  getBannerStreamUrl,
  getMediaUrl,
} from "../../services/catalogueService";
import "../../pages/Home.css";

const BANNER_REFRESH_INTERVAL_MS = 10000;
const BANNER_RETRY_INTERVAL_MS = 5000;
const legacyBannerItems = Array.from({ length: 10 }, (_, index) => ({
  _id: `legacy-banner-${index + 1}`,
  imageUrl: `https://www.vsjewelcast.com/images/banner${index + 1}.jpg`,
  title: `VS Jewel Cast Banner ${index + 1}`,
}));

export default function HomeHero() {
  const [banners, setBanners] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isTransitionEnabled, setIsTransitionEnabled] = useState(true);

  const fetchBanners = useCallback(async (onRetry) => {
    try {
      const data = await getBanners();
      const nextBanners = Array.isArray(data) ? data : [];

      setBanners((prev) => {
        const prevSerialized = JSON.stringify(prev);
        const nextSerialized = JSON.stringify(nextBanners);

        return prevSerialized === nextSerialized ? prev : nextBanners;
      });
    } catch (error) {
      console.error("Failed to load banners:", error);
      if (typeof onRetry === "function") {
        onRetry();
      }
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    let retryTimeoutId = null;

    const loadBanners = () => {
      fetchBanners(() => {
        if (isMounted) {
          if (retryTimeoutId) {
            window.clearTimeout(retryTimeoutId);
          }
          retryTimeoutId = window.setTimeout(loadBanners, BANNER_RETRY_INTERVAL_MS);
        }
      });
    };

    loadBanners();

    const intervalId = window.setInterval(loadBanners, BANNER_REFRESH_INTERVAL_MS);

    const handleWindowFocus = () => {
      loadBanners();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        loadBanners();
      }
    };

    const handleOnline = () => {
      loadBanners();
    };

    const bannerEvents = new EventSource(getBannerStreamUrl());
    bannerEvents.addEventListener("banner-updated", () => {
      loadBanners();
    });
    bannerEvents.onerror = () => {
      bannerEvents.close();
      if (isMounted) {
        if (retryTimeoutId) {
          window.clearTimeout(retryTimeoutId);
        }
        retryTimeoutId = window.setTimeout(loadBanners, BANNER_RETRY_INTERVAL_MS);
      }
    };

    window.addEventListener("focus", handleWindowFocus);
    window.addEventListener("online", handleOnline);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      isMounted = false;
      if (retryTimeoutId) {
        window.clearTimeout(retryTimeoutId);
      }
      window.clearInterval(intervalId);
      bannerEvents.close();
      window.removeEventListener("focus", handleWindowFocus);
      window.removeEventListener("online", handleOnline);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [fetchBanners]);

  useEffect(() => {
    const sourceBanners = banners.length ? banners : legacyBannerItems;
    if (!sourceBanners.length) return;

    // If we are at the clone (index === itemCount), we need to jump back to 0 instantly after the slide finishes.
    if (activeIndex === sourceBanners.length) {
      const jumpTimer = window.setTimeout(() => {
        setIsTransitionEnabled(false);
        setActiveIndex(0);
      }, 750); // Slightly longer than the 700ms transition
      return () => window.clearTimeout(jumpTimer);
    }

    // Normal auto-slide timer
    const currentBanner = sourceBanners[activeIndex] || sourceBanners[0];
    const durationMs = Math.max(Number(currentBanner?.displayDuration) || 3, 1) * 1000;

    const timer = window.setTimeout(() => {
      setIsTransitionEnabled(true);
      setActiveIndex((prev) => prev + 1);
    }, durationMs);

    return () => window.clearTimeout(timer);
  }, [activeIndex, banners]);

  const bannerItems = useMemo(() => {
    if (!banners.length) {
      return [...legacyBannerItems, { ...legacyBannerItems[0], _id: "legacy-banner-clone" }];
    }

    const normalizedBanners = banners.map((item, index) => ({
      _id: item._id || `banner-${index}`,
      imageUrl: (() => {
        const resolvedUrl = getMediaUrl(item.imageUrl);
        if (!resolvedUrl) {
          return banner;
        }

        const version = encodeURIComponent(
          item.updatedAt || item.createdAt || item._id || index
        );

        return `${resolvedUrl}${resolvedUrl.includes("?") ? "&" : "?"}v=${version}`;
      })(),
      title: item.title || "VS Jewel Cast Banner",
    }));

    return [
      ...normalizedBanners,
      {
        ...normalizedBanners[0],
        _id: `${normalizedBanners[0]._id}-clone`,
      },
    ];
  }, [banners]);

  const handleBannerTransitionEnd = () => {
    // The jump logic is now handled by setTimeout in the useEffect to avoid reverse slides, 
    // but we can still keep this stub to satisfy the linter if needed, or just let it be empty.
  };

  useEffect(() => {
    if (!isTransitionEnabled && activeIndex === 0) {
      // Force a reflow to ensure the instant jump is registered
      const frame = window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          setIsTransitionEnabled(true);
        });
      });
      return () => window.cancelAnimationFrame(frame);
    }
  }, [isTransitionEnabled, activeIndex]);

  return (
    <div className="container">
      <section className="banner-section">
        <div className="banner-container">
          <div
            className="banner-track"
            style={{
              transform: `translateX(-${activeIndex * 100}%)`,
              transition: isTransitionEnabled
                ? "transform 0.7s ease-in-out"
                : "none",
            }}
            onTransitionEnd={handleBannerTransitionEnd}
          >
            {bannerItems.map((item) => (
              <div className="banner-slide" key={item._id}>
                <img
                  src={item.imageUrl}
                  alt={item.title}
                  className="hero-banner-img"
                />
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
